/**
 * useTerminalSize Hook
 * Provides reactive terminal dimensions that update on resize
 * Uses Ink's useStdout for proper integration with the terminal
 */

import { useStdout } from 'ink';
import { useState, useEffect } from 'react';

export interface TerminalSize {
    columns: number;
    rows: number;
}

/**
 * Hook that returns current terminal dimensions and re-renders on resize
 */
export const useTerminalSize = (): TerminalSize => {
    const { stdout } = useStdout();
    const [size, setSize] = useState<TerminalSize>({
        columns: stdout.columns || 80,
        rows: stdout.rows || 24,
    });

    useEffect(() => {
        const handleResize = () => {
            setSize({
                columns: stdout.columns || 80,
                rows: stdout.rows || 24,
            });
        };

        // Update on resize
        stdout.on('resize', handleResize);

        // Initial size update in case stdout dimensions changed
        handleResize();

        return () => {
            stdout.off('resize', handleResize);
        };
    }, [stdout]);

    return size;
};
